import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface Employee {
  id: string;
  name: string;
  firstname: string;
  lastname: string;
  email: string;
  birth_date: string;
  address: string;
  city: string;
  postal_code: string;
  country: string;
  ahv_number: string;
  iban: string;
  bank_name: string;
  health_insurance_provider: string;
  work_permit_type: string;
  work_permit_expires: string;
  nationality: string;
}

interface ContractData {
  contract_id: string;
  employee: Employee;
  contract_type: "hourly" | "monthly";
  start_date: string;
  end_date?: string;
  hourly_rate?: number;
  monthly_salary?: number;
  working_hours_per_week: number;
  probation_period_months: number;
  vacation_days: number;
  notice_period_days: number;
  job_title: string;
  department?: string;
  job_description?: string;
  employee_id: string;
}

interface PDFHelperMethods {
  pdf: jsPDF;
  pageHeight: number;
  margin: number;
  currentY: number;
  pageNumber: number;
}

class PDFGenerator {
  private pdf: jsPDF;
  private pageHeight: number;
  private margin: number = 20;
  private currentY: number = 30;
  private pageNumber: number = 1;

  constructor() {
    this.pdf = new jsPDF();
    this.pageHeight = this.pdf.internal.pageSize.height;
  }

  // Calculate text height for given content and width
  private calculateTextHeight(text: string, fontSize: number, maxWidth: number): number {
    this.pdf.setFontSize(fontSize);
    const lines = this.pdf.splitTextToSize(text, maxWidth);
    return lines.length * (fontSize * 0.4) + 2; // Add small padding
  }

  // Check if content fits on current page
  private checkPageBreak(contentHeight: number, minSpaceRequired: number = 40): boolean {
    return (this.currentY + contentHeight + minSpaceRequired) > (this.pageHeight - this.margin);
  }

  // Add a new page with header
  private addNewPage(): void {
    this.pdf.addPage();
    this.pageNumber++;
    this.currentY = 30;
    
    // Add page header
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text('Walia Solutions - Arbeitsvertrag', this.margin, 20);
    this.pdf.text(`Seite ${this.pageNumber}`, this.pdf.internal.pageSize.width - this.margin - 20, 20);
    
    // Add separator line
    this.pdf.setLineWidth(0.1);
    this.pdf.line(this.margin, 25, this.pdf.internal.pageSize.width - this.margin, 25);
    
    this.currentY = 35;
  }

  // Add wrapped text with automatic line breaking
  private addWrappedText(text: string, x: number, y: number, maxWidth: number, fontSize: number = 10): number {
    this.pdf.setFontSize(fontSize);
    const lines = this.pdf.splitTextToSize(text, maxWidth);
    this.pdf.text(lines, x, y);
    return y + (lines.length * (fontSize * 0.4));
  }

  // Add a section with automatic page break management
  private addSectionWithPageBreak(title: string, content: string, isNumberedSection: boolean = true): void {
    const maxWidth = this.pdf.internal.pageSize.width - 2 * this.margin;
    
    // Calculate total height needed for this section
    const titleHeight = 12; // Bold title with spacing
    const contentHeight = this.calculateTextHeight(content, 10, maxWidth);
    const totalSectionHeight = titleHeight + contentHeight + 15; // Extra spacing after section
    
    // For numbered sections, ensure they don't split across pages
    if (isNumberedSection && this.checkPageBreak(totalSectionHeight, 20)) {
      this.addNewPage();
    }
    // For non-numbered sections, check with smaller buffer
    else if (!isNumberedSection && this.checkPageBreak(totalSectionHeight, 10)) {
      this.addNewPage();
    }

    // Add the section title
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(title, this.margin, this.currentY);
    this.currentY += 8;
    
    // Add the section content
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(10);
    this.currentY = this.addWrappedText(content, this.margin, this.currentY, maxWidth);
    this.currentY += 10; // Add spacing after section
  }

  // Generate the complete contract PDF
  generateContract(data: ContractData): void {
    // Header with company information
    this.pdf.setFontSize(14);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Walia Solutions', this.margin, this.currentY);
    this.currentY += 6;

    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text('Brunnenwiesenstrasse 44', this.margin, this.currentY);
    this.currentY += 4;
    this.pdf.text('CH-8610 Uster', this.margin, this.currentY);
    this.currentY += 4;
    this.pdf.text('+41 76 698 72 32', this.margin, this.currentY);
    this.currentY += 4;
    this.pdf.text('info@walia-solutions.ch', this.margin, this.currentY);
    this.currentY += 15;

    // Date and title
    const today = new Date().toLocaleDateString('de-CH');
    this.pdf.text(`Uster, ${today}`, this.margin, this.currentY);
    this.currentY += 15;

    this.pdf.setFontSize(16);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('ARBEITSVERTRAG', this.margin, this.currentY);
    this.currentY += 15;

    // Contract parties section
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Zwischen', this.margin, this.currentY);
    this.currentY += 8;

    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    const maxWidth = this.pdf.internal.pageSize.width - 2 * this.margin;
    
    this.currentY = this.addWrappedText(
      'Walia Solutions, Brunnenwiesenstrasse 44, 8610 Uster\n(nachfolgend "Arbeitgeber" genannt)',
      this.margin, this.currentY, maxWidth
    );
    this.currentY += 8;

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('und', this.margin, this.currentY);
    this.currentY += 8;

    this.pdf.setFont('helvetica', 'normal');
    const employeeInfo = `${data.employee.firstname} ${data.employee.lastname}, geboren am ${data.employee.birth_date ? new Date(data.employee.birth_date).toLocaleDateString('de-CH') : 'nicht angegeben'}, ${data.employee.address}, ${data.employee.postal_code} ${data.employee.city}, ${data.employee.country}\nAHV-Nr.: ${data.employee.ahv_number || 'nicht angegeben'}\n(nachfolgend "Arbeitnehmer/in" genannt)`;
    this.currentY = this.addWrappedText(employeeInfo, this.margin, this.currentY, maxWidth);
    this.currentY += 8;

    this.currentY = this.addWrappedText('wird folgender Arbeitsvertrag geschlossen:', this.margin, this.currentY, maxWidth);
    this.currentY += 15;

    // Contract sections with proper page break management
    this.addSectionWithPageBreak(
      '1. Vertragsgegenstand und Tätigkeit',
      `Der/Die Arbeitnehmer/in wird als ${data.job_title} in der Abteilung ${data.department || 'Administration'} eingestellt. ${data.job_description || 'Die Aufgaben werden gemäss den Vorgaben und Qualitätsstandards des Arbeitgebers ausgeführt.'}`
    );

    // Enhanced salary and working hours section
    let salaryText = '';
    if (data.contract_type === 'hourly') {
      salaryText = `Die Vergütung beträgt CHF ${data.hourly_rate?.toFixed(2)} pro Stunde brutto. Die maximale wöchentliche Arbeitszeit beträgt ${data.working_hours_per_week} Stunden. Der/Die Arbeitnehmer/in ist in der Zeiteinteilung frei, solange die vereinbarten Arbeitszeiten eingehalten werden.`;
    } else {
      salaryText = `Die Vergütung beträgt CHF ${data.monthly_salary?.toFixed(2)} pro Monat brutto bei einer wöchentlichen Arbeitszeit von ${data.working_hours_per_week} Stunden.`;
    }
    
    salaryText += ` Die Lohnzahlung erfolgt monatlich auf das Bankkonto bei ${data.employee.bank_name || 'der angegebenen Bank'}, IBAN: ${data.employee.iban || 'wird separat mitgeteilt'}. Bezahlte Pause gem. OR: Ab 5.5 Stunden am gleichen Tag, 15 Minuten. Die Ferien-Vergütung ist im ${data.contract_type === 'hourly' ? 'Stundenlohn' : 'Monatslohn'} beinhaltet.`;

    this.addSectionWithPageBreak('2. Vergütung, Arbeitszeit und Lohnzahlung', salaryText);

    // Enhanced social insurance section
    let socialInsuranceText = `Von der Bruttovergütung werden die gesetzlichen Abzüge für AHV/IV/EO in Höhe von 5,15% sowie für die Arbeitslosenversicherung (ALV) abgezogen. Der Arbeitgeber übernimmt die Hälfte der Sozialversicherungsbeiträge gemäss den gesetzlichen Bestimmungen.`;
    
    if (data.employee.ahv_number) {
      socialInsuranceText += ` AHV-Nummer des Arbeitnehmers: ${data.employee.ahv_number}.`;
    }

    this.addSectionWithPageBreak('3. Sozialversicherungsbeiträge', socialInsuranceText);

    const contractDuration = data.end_date 
      ? `Dieser Vertrag tritt am ${new Date(data.start_date).toLocaleDateString('de-CH')} in Kraft und läuft bis zum ${new Date(data.end_date).toLocaleDateString('de-CH')}.`
      : `Dieser Vertrag tritt am ${new Date(data.start_date).toLocaleDateString('de-CH')} in Kraft und läuft auf unbestimmte Zeit.`;
      
    const probationText = data.probation_period_months > 0 
      ? ` Die Probezeit beträgt ${data.probation_period_months} Monate.` 
      : '';
      
    this.addSectionWithPageBreak(
      '4. Vertragsdauer und Kündigung',
      `${contractDuration}${probationText} Der Vertrag kann von beiden Parteien schriftlich mit einer Frist von ${data.notice_period_days} Tagen gekündigt werden. Während der Probezeit kann der Vertrag von beiden Seiten jederzeit mit einer Frist von 7 Tagen gekündigt werden.`
    );

    this.addSectionWithPageBreak(
      '5. Ferien',
      `Der/Die Arbeitnehmer/in hat Anspruch auf ${data.vacation_days} Ferientage pro Jahr (bei 100% Arbeitspensum). Bei Teilzeitarbeit werden die Ferientage entsprechend dem Beschäftigungsgrad angepasst. Die Ferien sind grundsätzlich im Kalenderjahr zu beziehen, in dem sie entstehen.`
    );

    // Enhanced duties section with insurance information
    let employeeDutiesText = `Gewissenhafte Ausführung der übertragenen Aufgaben gemäss den Vorgaben des Arbeitgebers. Einhaltung der Arbeitszeiten und Termine. Vertraulicher Umgang mit allen Informationen des Arbeitgebers. Meldung von Krankheit und Unfall innert 24 Stunden.`;
    
    if (data.employee.health_insurance_provider) {
      employeeDutiesText += ` Der/Die Arbeitnehmer/in ist bei ${data.employee.health_insurance_provider} krankenversichert.`;
    }

    this.addSectionWithPageBreak('6. Pflichten des Arbeitnehmers/der Arbeitnehmerin', employeeDutiesText);

    this.addSectionWithPageBreak(
      '7. Pflichten des Arbeitgebers',
      'Rechtzeitige Bereitstellung notwendiger Informationen und Arbeitsmittel. Fristgerechte Zahlung der vereinbarten Vergütung. Führung der Lohnbuchhaltung und Abrechnung der Sozialversicherungsbeiträge. Bei Krankheit oder Unfall: Lohnfortzahlung gemäss den gesetzlichen Bestimmungen.'
    );

    this.addSectionWithPageBreak(
      '8. Versicherungen',
      'Der Arbeitgeber schliesst eine Unfallversicherung für den/die Arbeitnehmer/in ab, welche die Risiken bei Berufs- und Nichtberufsunfällen sowie Berufskrankheiten deckt. Die Prämien für die Berufsunfallversicherung gehen zu Lasten des Arbeitgebers, diejenigen für die Nichtberufsunfallversicherung zu Lasten des Arbeitnehmers.'
    );

    // Work permit section for non-Swiss employees
    if (data.employee.nationality && data.employee.nationality !== 'Swiss' && data.employee.work_permit_type) {
      const workPermitText = `Der/Die Arbeitnehmer/in verfügt über eine gültige Arbeitsbewilligung vom Typ ${data.employee.work_permit_type}${data.employee.work_permit_expires ? `, gültig bis ${new Date(data.employee.work_permit_expires).toLocaleDateString('de-CH')}` : ''}. Die Fortsetzung des Arbeitsverhältnisses ist abhängig von der Verlängerung der Arbeitsbewilligung.`;
      
      this.addSectionWithPageBreak('9. Arbeitsbewilligung', workPermitText);
    }

    this.addSectionWithPageBreak(
      '10. Abrechnung und Zahlungsbedingungen',
      'Die Abrechnung der erbrachten Leistungen erfolgt monatlich, jeweils am Monatsende. Der Lohn wird bis zum 25. des Folgemonats auf das angegebene Bankkonto überwiesen. Bei verspäteter Zahlung sind Verzugszinsen geschuldet.'
    );

    this.addSectionWithPageBreak(
      '11. Schlussbestimmungen',
      'Änderungen und Ergänzungen dieses Vertrages bedürfen der Schriftform. Sollten einzelne Bestimmungen dieses Vertrages unwirksam sein, bleibt die Gültigkeit der übrigen Bestimmungen unberührt. Dieser Vertrag unterliegt dem Schweizer Recht. Gerichtsstand ist Uster.'
    );

    // Signature section - ensure it has enough space
    const signatureHeight = 80;
    if (this.checkPageBreak(signatureHeight, 20)) {
      this.addNewPage();
    }

    this.currentY += 20;

    // Enhanced signature section
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    const signatureY = this.currentY;
    
    this.pdf.text('Ort und Datum: ________________', this.margin, signatureY);
    this.pdf.text('Ort und Datum: ________________', this.pdf.internal.pageSize.width - this.margin - 80, signatureY);
    
    this.pdf.text('Arbeitgeber:', this.margin, signatureY + 20);
    this.pdf.text('Arbeitnehmer/in:', this.pdf.internal.pageSize.width - this.margin - 80, signatureY + 20);
    
    this.pdf.text('Walia Solutions', this.margin, signatureY + 30);
    this.pdf.text(`${data.employee.firstname} ${data.employee.lastname}`, this.pdf.internal.pageSize.width - this.margin - 80, signatureY + 30);
    
    this.pdf.text('_____________________', this.margin, signatureY + 45);
    this.pdf.text('_____________________', this.pdf.internal.pageSize.width - this.margin - 80, signatureY + 45);

    // Add footer on last page
    if (this.pageNumber > 1) {
      this.pdf.setFontSize(8);
      this.pdf.text(`Arbeitsvertrag - Seite ${this.pageNumber} von ${this.pageNumber}`, 
        this.margin, this.pageHeight - 10);
    }
  }

  // Save the PDF
  save(filename: string): void {
    this.pdf.save(filename);
  }
}

export const generateJobContractPDF = async (data: ContractData) => {
  const generator = new PDFGenerator();
  generator.generateContract(data);
  
  const fileName = `Arbeitsvertrag_${data.employee.lastname}_${data.employee.firstname}_${new Date().toISOString().split('T')[0]}.pdf`;
  generator.save(fileName);
};
